import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { groupModule } from '@/store/group/module'
import { isPromise } from '@/utils'
import type { Study, Teach } from '@pocketprep/types'
import type { TStudent } from '@/store/student/types'
import * as Sentry from '@sentry/vue'

const getCurrentOrgStudentUEM = () => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgStudentUEM = currentOrganization
        && studentModule.state.currentOrgStudentUEM[currentOrganization.objectId]?.value

    if (currentOrgStudentUEM && !(isPromise(currentOrgStudentUEM))) {
        return currentOrgStudentUEM
    } else {
        return {}
    }
}

const getCurrentOrgStudentUsers = (): { [studentUserId: string]: Study.Class.UserJSON } => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgStudentUsers = currentOrganization
        && studentModule.state.currentOrgStudentUsers[currentOrganization.objectId]?.value

    if (currentOrgStudentUsers && !(isPromise(currentOrgStudentUsers))) {
        return currentOrgStudentUsers
    } else {
        return {}
    }
}

const getCurrentOrgStudents = (): { [studentId: string]: TStudent } => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgStudents = currentOrganization
        && studentModule.state.currentOrgStudents[currentOrganization.objectId]?.value

    if (currentOrgStudents && !(isPromise(currentOrgStudents))) {
        return currentOrgStudents
    } else {
        return {}
    }
}

const getCurrentGroupStudents = (params?: {
    excludePendingStudents: boolean
}) => {
    const currentOrgStudents = getCurrentOrgStudents()
    const currentGroup = groupModule.getters.getCurrentGroup()
    const currentGroupStudentIds = currentGroup?.orgStudents.map(orgStudent => orgStudent.objectId)

    if (currentGroupStudentIds) {
        const students = currentGroupStudentIds
            .map(id => currentOrgStudents[id])
            .filter(student => !!student)

        // If the caller passes params and explicitly requests NO pending students, filter them out
        return (params?.excludePendingStudents) ? students.filter(s => s.user) : students
    } else {
        return []
    }
}

const getCurrentStudent = (): Teach.Class.OrgStudentJSON | null => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgStudents = currentOrganization
        && studentModule.state.currentOrgStudents[currentOrganization.objectId]?.value

    const currentStudentId = studentModule.state.currentStudentId
    if (currentOrgStudents && !(isPromise(currentOrgStudents)) && currentStudentId) {
        return currentOrgStudents[currentStudentId] || null
    } else {
        return null
    }
}

const getCurrentStudentQuizById = (quizId: string) => {
    const quizzes = studentModule.state.currentOrgStudentQuizzes
    return quizzes.find(quiz => quiz.objectId === quizId) || null
}

const getStudentsForOrgExamId = (orgExamId: string) => {
    const currentOrgStudents = getCurrentOrgStudents()
    const studentsByExam = groupModule.getters.searchCurrentOrgGroups({ examId: orgExamId, instructorId: null })
    const studentsInOrgExam = studentsByExam.flatMap(exam => exam.orgStudents)

    return studentsInOrgExam.reduce<TStudent[]>((acc, student) => {
        const studentInOrgExam = currentOrgStudents[student.objectId]
        if (!studentInOrgExam) {
            Sentry.captureException(new Error(`getStudentsForOrgExamId: missing student id ${student.objectId}`))
        } else {
            acc.push(studentInOrgExam)
        }
        return acc
    }, [])
}

export default {
    getCurrentOrgStudentUEM,
    getCurrentOrgStudentUsers,
    getCurrentOrgStudents,
    getCurrentGroupStudents,
    getCurrentStudent,
    getCurrentStudentQuizById,
    getStudentsForOrgExamId,
}
